import { useQuery } from '@tanstack/react-query'
import { Link, ModuleHeader, SpinnerWithBackground, TabBar, Text } from '@viapeople/components'
import { useCallback } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { trpc } from '../../utils/trpc'
import type { Organization } from './types'

export function OrganizationDetailsPage() {
	const { organizationId } = useParams<{ organizationId: string }>()
	const navigate = useNavigate()
	const location = useLocation()
	const pathSegments = location.pathname.split('/').filter(Boolean)
	const lastSegment = pathSegments[pathSegments.length - 1]

	const { data, isLoading, isError } = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId ?? '' }),
		enabled: !!organizationId,
	})
	const organization = data as Organization | undefined

	const { data: tabsData } = useQuery({
		...trpc.admin.organizations.getAvailableTabs.queryOptions({ organizationId: organizationId ?? '' }),
		enabled: !!organizationId,
	})

	const tabs = tabsData?.tabs ?? [
		{ id: 'overview', label: 'Overview' },
		{ id: 'profile', label: 'Profile' },
	]
	const currentTab = tabs.some((tab) => tab.id === lastSegment) ? lastSegment : 'overview'

	const handleTabChange = useCallback(
		(tabId: string) => {
			navigate(`/organizations/${organizationId}/${tabId}`)
		},
		[navigate, organizationId],
	)

	if (isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	if (isError || !organization) {
		return (
			<main className='p-6'>
				<Text>Error loading organization details. Please try again.</Text>
				<Link href='/organizations' variant='default'>
					Back to Organizations
				</Link>
			</main>
		)
	}

	return (
		<main>
			<div className='p-6'>
				<ModuleHeader
					breadcrumb={[{ label: 'Organizations', to: '/organizations' }, { label: organization.name }]}
					title={`${organization.name} Organization`}
					logo={{
						url: organization.logo,
						name: organization.name,
					}}
				/>
				<TabBar tabs={tabs} activeTab={currentTab || 'overview'} onChange={handleTabChange} />
			</div>

			<Outlet context={{ organization }} />
		</main>
	)
}
